<script setup lang="ts">
import { ref, watch, nextTick, inject, computed } from 'vue'
import AppModal from '@/components/shared/AppModal.vue'
import QRCodeStyling from 'qr-code-styling'
import QRBorderPlugin from 'qr-border-plugin'
import { useBrandIcon } from '@/composables/useBrandIcon'
import { ThemeProvider, themeProviderKey } from '@/utils/theme.util'
import { useRoute } from 'vue-router'
import type { LogoData } from '@/types/client'
import { useResourcesStore } from '@/store/modules/resources'
import { Copy, Check } from 'lucide-vue-next'
import { getBaseUrl } from '@/utils/shareUrlUtils'

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const resourcesStore = useResourcesStore()
const route = useRoute()

const props = withDefaults(defineProps<{
  visible: boolean
  url: string
  displayUrl?: boolean
  logoData?: LogoData
  title?: string
  zIndex?: number
  urlLabel?: string
}>(), {
  displayUrl: true,
})

const qrContainer = ref<HTMLElement | null>(null)
const qrCode = ref<any>(null)

const { theme } = inject<ThemeProvider>(themeProviderKey) as ThemeProvider

function getCssVar(name: string, fallback: string) {
  const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return val || fallback
}

function getThemeColors() {
  const background = getCssVar('--card', '0 0% 100%')
  const foreground = getCssVar('--card-foreground', '240 10% 3.9%')
  const primary = getCssVar('--primary', '240 5.9% 10%')

  return {
    background: `hsl(${background})`,
    foreground: `hsl(${foreground})`,
    primary: `hsl(${primary})`,
  }
}

const copied = ref(false)
const displayUrlForBorder = computed(() => {
  if (!props.url) return 'SCAN ME!'
  try {
    const url = new URL(props.url);
    return url.origin;
  } catch (e) {
    console.warn('Failed to parse URL for border:', props.url);
    const base = getBaseUrl();
    try {
      return new URL(base).origin;
    } catch {
      return base;
    }
  }
});

function makeBorder() {
  const theme = getThemeColors()
  return QRBorderPlugin({
    round: 1,
    thickness: 35,
    color: theme.background,
    dasharray: '',
    borderInner: { thickness: 2, color: theme.foreground, dasharray: '' },
    borderOuter: { thickness: 4, color: theme.primary, dasharray: '' },
    decorations: {
      top: { type: 'text', value: props.title, style: `font:16px sans-serif; fill:${theme.foreground};` },
      bottom: { type: 'text', value: props.displayUrl ? displayUrlForBorder.value : 'SCAN ME!', style: `font:16px sans-serif; fill:${theme.foreground};` },
    } as any,
  })
}
async function getLogoWithBackground(): Promise<string> {
  return useBrandIcon({
    context: props.logoData?.context,
    background: props.logoData?.background,
    cornerRadius: 128 // Circle
  })
}

async function renderQrCode() {
  if (!props.url || !qrContainer.value) {
    return
  }

  const theme = getThemeColors()
  const logo = await getLogoWithBackground()

  const paddedData = props.url.padEnd(25,' ')
  
  if (!qrCode.value) {
    qrCode.value = new QRCodeStyling({
      shape: 'circle',
      width: 375,
      height: 375,
      margin: 30,
      image: logo,
      qrOptions: { errorCorrectionLevel: 'H' },
      imageOptions: { imageSize: 0.5, margin: 2, hideBackgroundDots: true },
      data: paddedData,
      dotsOptions: { color: theme.foreground, type: 'classy-rounded' },
      backgroundOptions: { color: theme.background },
    })
  } else {
    qrCode.value.update({
      data: paddedData,
      image: logo,
      dotsOptions: { color: theme.foreground, type: 'classy-rounded' },
      backgroundOptions: { color: theme.background },
    })
  }

  qrCode.value.applyExtension(makeBorder())

  if (qrContainer.value) {
    qrContainer.value.innerHTML = ''
    qrCode.value.append(qrContainer.value)
  }
}

watch(() => props.visible, async (visible) => {
  if (visible) {
    await nextTick()
    setTimeout(() => renderQrCode(), 150)
  }
})

watch(theme, () => {
  if (props.visible && qrCode.value) {
    renderQrCode()
  }
})

watch(() => props.url, async () => {
  if (props.visible) {
    await nextTick()
    renderQrCode()
  }
})

async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(props.url)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

function close() {
  emit('update:visible', false)
}
</script>

<template>
  <AppModal
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    @close="close"
    :title="props.title || 'Scan to Share'"
    max-width="max-w-xl"
    main-class="p-0"
    :z-index="props.zIndex"
  >
    <div class="flex flex-col items-center justify-center p-8 bg-gray-50/50 dark:bg-black/20 min-h-[400px]">
      <div v-show="props.url" ref="qrContainer" class="w-auto h-auto bg-white dark:bg-gray-800 p-2 rounded-3xl shadow-xl border border-border animate-in zoom-in duration-500"></div>
      <div v-show="!props.url" class="flex flex-col items-center gap-4 text-gray-400 italic">
        <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p>Generating QR Code...</p>
      </div>
    </div>
    
    <div v-if="props.displayUrl" class="px-8 pb-10 flex flex-col items-center">
      <div class="w-full bg-white dark:bg-zinc-900 border border-border rounded-2xl p-4 flex items-center justify-between gap-4 group hover:border-primary/50 transition-colors shadow-sm">
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{{ props.urlLabel || 'URL' }}</p>
          <p class="text-sm font-mono text-gray-600 dark:text-gray-300 truncate">{{ props.url }}</p>
        </div>
        <button 
          @click="copyToClipboard"
          class="shrink-0 p-3 rounded-xl bg-gray-50 dark:bg-white/5 hover:bg-primary hover:text-white transition-all flex items-center justify-center border border-transparent hover:shadow-lg hover:shadow-primary/20"
          :title="copied ? 'Copied!' : 'Copy URL'"
        >
          <Transition name="scale" mode="out-in">
            <Check v-if="copied" class="w-5 h-5" key="check" />
            <Copy v-else class="w-5 h-5" key="copy" />
          </Transition>
        </button>
      </div>
    </div>
  </AppModal>
</template>
<style scoped>
.scale-enter-active,
.scale-leave-active {
  transition: all 0.2s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>
